import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import { WithData, WithMeta, Nullable, UserWithToken, CardRarity, CardLanguage, CardState } from './api.types';

// =============================================================================
// TYPES
// =============================================================================

export type OrderStatus =
  // Borrador de un pedido manual: sólo lo ve el vendedor que lo está armando.
  | 'DRAFT'
  | 'PENDING'
  | 'CONFIRMED'
  | 'PAID'
  | 'SHIPPED'
  | 'DELIVERED'
  | 'COMPLETED'
  | 'CANCELLED';

export type OrderEventType =
  | 'CREATED'
  | 'CONFIRMED'
  | 'REJECTED'
  | 'PAYMENT_CONFIRMED'
  | 'SHIPPED'
  | 'DELIVERED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'MESSAGE'
  | 'SHIPPING_UPDATED'
  | 'UPDATED';

export interface OrderParticipant {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  picture: string | null;
}

export interface OrderItem {
  id: number;
  inventoryId: number | null;
  listingId: number | null;
  bundleId: number | null;
  /** Lo que no es una carta: un accesorio o un mazo publicado. */
  accessoryListingId: number | null;
  deckListingId: number | null;
  /** Nulos cuando el ítem no es una carta. */
  konamiId: number | null;
  cardSetId: number | null;
  cardName: string;
  setName: string;
  setCode: string;
  /** Nula cuando el ítem no es una carta. */
  rarity: string | null;
  language: string;
  state: string;
  quantity: number;
  /** En la moneda del ítem. */
  unitPrice: string;
  totalPrice: string;
  /** Moneda del ítem: la del pedido salvo que se le haya puesto una propia. */
  currency: Currency;
  exchangeRate: number;
  /** `totalPrice` en ARS, congelado al guardar. */
  convertedTotalPrice: string;
  imageUrl: string | null;
  listingRemoved: boolean | null;
}

/** Cuánto suma el pedido en cada moneda en la que se cargó algo. */
export interface OrderCurrencyTotal {
  currency: Currency;
  itemsTotal: string;
  shippingTotal: string;
  total: string;
  convertedTotal: string;
}

export interface OrderEvent {
  id: number;
  orderId: number;
  type: OrderEventType;
  message: string | null;
  /** Payload JSON libre del evento; su forma depende del `type`. */
  metadata: unknown;
  user: OrderParticipant;
  createdAt: string;
}

export interface OrderReview {
  id: number;
  orderId: number;
  communicationRating: number;
  shippingRating: number;
  productRating: number;
  overallRating: number;
  communicationComment: string | null;
  shippingComment: string | null;
  productComment: string | null;
  generalComment: string | null;
  createdAt: string;
}

export interface OrderReviewInput {
  communicationRating: number;
  shippingRating: number;
  productRating: number;
  communicationComment?: string;
  shippingComment?: string;
  productComment?: string;
  generalComment?: string;
}

export interface OrderBuyerReview {
  id: number;
  orderId: number;
  communicationRating: number;
  paymentCommitmentRating: number;
  deliveryCommitmentRating: number;
  overallRating: number;
  communicationComment: string | null;
  paymentCommitmentComment: string | null;
  deliveryCommitmentComment: string | null;
  generalComment: string | null;
  createdAt: string;
}

export interface OrderBuyerReviewInput {
  communicationRating: number;
  paymentCommitmentRating: number;
  deliveryCommitmentRating: number;
  communicationComment?: string;
  paymentCommitmentComment?: string;
  deliveryCommitmentComment?: string;
  generalComment?: string;
}

export interface SellerRatings {
  totalReviews: number;
  averageCommunication: number;
  averageShipping: number;
  averageProduct: number;
  averageOverall: number;
}

export interface BuyerRatings {
  totalReviews: number;
  averageCommunication: number;
  averagePaymentCommitment: number;
  averageDeliveryCommitment: number;
  averageOverall: number;
}

export interface Order {
  id: number;
  buyer: OrderParticipant;
  seller: OrderParticipant;
  status: OrderStatus;
  /** Cargado a mano por el vendedor: es lo que habilita editarlo y borrarlo. */
  isManual: boolean;
  /** Moneda general del pedido; los ítems la heredan salvo override. */
  currency: Currency;
  exchangeRate: number;
  /** Siempre en ARS. */
  subtotal: string;
  shippingCost: string;
  total: string;
  /** Lo mismo abierto por moneda, para mostrar en qué se pactó cada parte. */
  totalsByCurrency: OrderCurrencyTotal[];
  paymentMethod: string | null;
  shippingMethod: string | null;
  shippingAddress: string | null;
  trackingNumber: string | null;
  buyerNotes: string | null;
  sellerNotes: string | null;
  items: OrderItem[];
  events: OrderEvent[];
  shipments: OrderShipment[];
  review: OrderReview | null;
  buyerReview: OrderBuyerReview | null;
  confirmedAt: string | null;
  paidAt: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Fechas de los hitos de un pedido, para `overrideOrderStatus`.
 *
 * Cada campo distingue ausente de `null`: ausente deja la fecha como está,
 * `null` la borra. Sin eso no habría forma de sacar una fecha cargada mal sin
 * mover el estado del pedido.
 */
export interface OrderMilestoneDatesInput {
  createdAt?: string | null;
  confirmedAt?: string | null;
  paidAt?: string | null;
  shippedAt?: string | null;
  deliveredAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
}

export interface OrderSummary {
  totalOrders: number;
  pendingOrders: number;
  confirmedOrders: number;
  completedOrders: number;
  cancelledOrders: number;
}

export interface CreateOrderInput {
  sellerId: number;
  // expectedUnitPrice: el precio por unidad que vio el comprador. Si la
  // publicación cambió de precio (precio automático), el API rechaza el pedido
  // con `price_changed` — ver parsePriceChangedError.
  // Cada ítem trae exactamente uno de los cuatro orígenes.
  items: {
    listingId?: number;
    bundleId?: number;
    accessoryListingId?: number;
    deckListingId?: number;
    quantity: number;
    expectedUnitPrice?: number;
  }[];
  paymentMethod?: string;
  shippingMethod?: string;
  shippingAddress?: string;
  buyerNotes?: string;
  buyerPhone?: string;
}

export interface OrdersFilters {
  status?: OrderStatus;
  role?: 'buyer' | 'seller';
  page?: number;
  limit?: number;
}

// Moneda de un tramo de envío. El pedido siempre se totaliza en ARS: cada
// tramo guarda su moneda original + la cotización con la que se convirtió.
export type Currency = 'USD' | 'ARS' | 'EUR';

// Un tramo del recorrido del envío. Un pedido puede tener varios (Italia ->
// España -> Argentina) y cada uno se paga en su propia moneda.
export interface OrderShipment {
  id: number;
  sequence: number;
  origin: string;
  destination: string;
  carrier: string | null;
  trackingNumber: string | null;
  cost: string;
  currency: Currency;
  exchangeRate: number;
  convertedCost: string;
  paidAt: string | null;
  notes: string | null;
  createdAt: string;
}

export interface OrderShipmentInput {
  // Ignorado en addOrderShipment (se asigna el siguiente número de la
  // secuencia); respetado en updateOrderShipment.
  sequence?: number;
  origin: string;
  destination: string;
  carrier?: string;
  trackingNumber?: string;
  cost: number;
  currency: Currency;
  exchangeRate: number;
  paidAt?: string;
  notes?: string;
}

export interface ManualOrderBuyerInput {
  name: string;
  lastName: string;
  email?: string;
  phone?: string;
}

export interface ManualOrderItemInput {
  konamiId: number;
  cardSetId: number;
  rarity?: CardRarity;
  language: CardLanguage;
  state: CardState;
  quantity: number;
  unitPrice: number;
  /** Sólo para la carta que va en otra moneda que el resto del pedido. */
  currency?: Currency;
  exchangeRate?: number;
}

// Carga manual de un pedido por parte del vendedor. Ni los ítems ni el
// comprador tienen por qué existir de antes: no se lee ni se toca el stock.
export interface CreateManualOrderInput {
  // Exactamente uno de los dos
  buyerId?: number;
  newBuyer?: ManualOrderBuyerInput;
  /**
   * Cargar la venta de otro. Sólo un admin lo puede mandar distinto de sí
   * mismo (`not_admin`): el pedido nace a nombre de ese vendedor y le aparece
   * a él. Mandar el propio id no necesita permisos.
   */
  sellerId?: number;
  items: ManualOrderItemInput[];
  shipments?: OrderShipmentInput[];
  /** Moneda general del pedido. Por defecto ARS con cotización 1. */
  currency?: Currency;
  exchangeRate?: number;
  status?: OrderStatus;
  paymentMethod?: string;
  shippingMethod?: string;
  shippingAddress?: string;
  sellerNotes?: string;
}

/**
 * Edición de un pedido manual todavía no confirmado. Todo opcional: lo que no
 * venga no se toca. `items` y `shipments` se reemplazan enteros.
 */
export interface UpdateManualOrderInput {
  /** Sólo mientras el pedido sea borrador. */
  buyerId?: number;
  newBuyer?: ManualOrderBuyerInput;
  items?: ManualOrderItemInput[];
  shipments?: OrderShipmentInput[];
  currency?: Currency;
  exchangeRate?: number;
  paymentMethod?: string;
  shippingMethod?: string;
  shippingAddress?: string;
  sellerNotes?: string;
}

// =============================================================================
// FRAGMENTS
// =============================================================================

const ORDER_PARTICIPANT_FRAGMENT = gql`
  fragment OrderParticipantFields on OrderParticipant {
    id
    name
    email
    phone
    picture
  }
`;

const ORDER_ITEM_FRAGMENT = gql`
  fragment OrderItemFields on OrderItem {
    id
    inventoryId
    listingId
    bundleId
    accessoryListingId
    deckListingId
    konamiId
    cardSetId
    cardName
    setName
    setCode
    rarity
    language
    state
    quantity
    unitPrice
    totalPrice
    currency
    exchangeRate
    convertedTotalPrice
    imageUrl
    listingRemoved
  }
`;

const ORDER_SHIPMENT_FRAGMENT = gql`
  fragment OrderShipmentFields on OrderShipment {
    id
    sequence
    origin
    destination
    carrier
    trackingNumber
    cost
    currency
    exchangeRate
    convertedCost
    paidAt
    notes
    createdAt
  }
`;

const ORDER_EVENT_FRAGMENT = gql`
  fragment OrderEventFields on OrderEvent {
    id
    orderId
    type
    message
    metadata
    user {
      ...OrderParticipantFields
    }
    createdAt
  }
`;

const ORDER_REVIEW_FRAGMENT = gql`
  fragment OrderReviewFields on OrderReview {
    id
    orderId
    communicationRating
    shippingRating
    productRating
    overallRating
    communicationComment
    shippingComment
    productComment
    generalComment
    createdAt
  }
`;

const ORDER_BUYER_REVIEW_FRAGMENT = gql`
  fragment OrderBuyerReviewFields on OrderBuyerReview {
    id
    orderId
    communicationRating
    paymentCommitmentRating
    deliveryCommitmentRating
    overallRating
    communicationComment
    paymentCommitmentComment
    deliveryCommitmentComment
    generalComment
    createdAt
  }
`;

const ORDER_FRAGMENT = gql`
  fragment OrderFields on Order {
    id
    buyer {
      ...OrderParticipantFields
    }
    seller {
      ...OrderParticipantFields
    }
    status
    isManual
    currency
    exchangeRate
    subtotal
    shippingCost
    total
    totalsByCurrency {
      currency
      itemsTotal
      shippingTotal
      total
      convertedTotal
    }
    paymentMethod
    shippingMethod
    shippingAddress
    trackingNumber
    buyerNotes
    sellerNotes
    items {
      ...OrderItemFields
    }
    events {
      ...OrderEventFields
    }
    shipments {
      ...OrderShipmentFields
    }
    review {
      ...OrderReviewFields
    }
    buyerReview {
      ...OrderBuyerReviewFields
    }
    confirmedAt
    paidAt
    shippedAt
    deliveredAt
    completedAt
    cancelledAt
    createdAt
    updatedAt
  }
  ${ORDER_PARTICIPANT_FRAGMENT}
  ${ORDER_ITEM_FRAGMENT}
  ${ORDER_EVENT_FRAGMENT}
  ${ORDER_SHIPMENT_FRAGMENT}
  ${ORDER_REVIEW_FRAGMENT}
  ${ORDER_BUYER_REVIEW_FRAGMENT}
`;

// =============================================================================
// QUERIES
// =============================================================================

// Get order by ID
export const getOrder = async (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<Order | null>>(
    gql`
      ${ORDER_FRAGMENT}
      query order($id: Int!) {
        data: order(id: $id) {
          ...OrderFields
        }
      }
    `,
    { id }
  );

// Get my orders
export const getMyOrders = async (user: Nullable<UserWithToken>, filters?: OrdersFilters) =>
  getGraphQLQueryClient(user).request<WithMeta<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      query myOrders($filters: OrdersFilters) {
        data: myOrders(filters: $filters) {
          data {
            ...OrderFields
          }
          meta {
            total
            perPage
            page
            lastPage
          }
        }
      }
    `,
    { filters }
  );

// Get orders summary
export const getOrdersSummary = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<{ data: OrderSummary }>(
    gql`
      query ordersSummary {
        data: ordersSummary {
          totalOrders
          pendingOrders
          confirmedOrders
          completedOrders
          cancelledOrders
        }
      }
    `
  );

// =============================================================================
// MUTATIONS
// =============================================================================

// Create order
export const createOrder = async (user: Nullable<UserWithToken>, input: CreateOrderInput) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation createOrder($input: CreateOrderInput!) {
        data: createOrder(input: $input) {
          ...OrderFields
        }
      }
    `,
    { input }
  );

// Create a manual order (seller). Does not touch stock.
export const createManualOrder = async (user: Nullable<UserWithToken>, input: CreateManualOrderInput) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation createManualOrder($input: CreateManualOrderInput!) {
        data: createManualOrder(input: $input) {
          ...OrderFields
        }
      }
    `,
    { input }
  );

// Edit a manual order while it is a draft or still pending (seller)
export const updateManualOrder = async (
  user: Nullable<UserWithToken>,
  orderId: number,
  input: UpdateManualOrderInput
) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation updateManualOrder($orderId: Int!, $input: UpdateManualOrderInput!) {
        data: updateManualOrder(orderId: $orderId, input: $input) {
          ...OrderFields
        }
      }
    `,
    { orderId, input }
  );

// Turn a draft into a real order the buyer can see (seller)
export const publishManualOrder = async (
  user: Nullable<UserWithToken>,
  orderId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation publishManualOrder($orderId: Int!) {
        data: publishManualOrder(orderId: $orderId) {
          ...OrderFields
        }
      }
    `,
    { orderId }
  );

// Discard a draft (seller)
export const deleteManualOrder = async (
  user: Nullable<UserWithToken>,
  orderId: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteManualOrder($orderId: Int!) {
        data: deleteManualOrder(orderId: $orderId)
      }
    `,
    { orderId }
  );

// Confirm order (seller)
export const confirmOrder = async (user: Nullable<UserWithToken>, orderId: number, message?: string) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation confirmOrder($orderId: Int!, $message: String) {
        data: confirmOrder(orderId: $orderId, message: $message) {
          ...OrderFields
        }
      }
    `,
    { orderId, message }
  );

// Reject order (seller)
export const rejectOrder = async (user: Nullable<UserWithToken>, orderId: number, reason: string) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation rejectOrder($orderId: Int!, $reason: String!) {
        data: rejectOrder(orderId: $orderId, reason: $reason) {
          ...OrderFields
        }
      }
    `,
    { orderId, reason }
  );

// Mark order as paid (buyer)
export const markOrderAsPaid = async (user: Nullable<UserWithToken>, orderId: number, message?: string) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation markOrderAsPaid($orderId: Int!, $message: String) {
        data: markOrderAsPaid(orderId: $orderId, message: $message) {
          ...OrderFields
        }
      }
    `,
    { orderId, message }
  );

// Mark order as shipped (seller)
export const markOrderAsShipped = async (
  user: Nullable<UserWithToken>,
  orderId: number,
  trackingNumber?: string,
  message?: string
) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation markOrderAsShipped(
        $orderId: Int!
        $trackingNumber: String
        $message: String
      ) {
        data: markOrderAsShipped(
          orderId: $orderId
          trackingNumber: $trackingNumber
          message: $message
        ) {
          ...OrderFields
        }
      }
    `,
    { orderId, trackingNumber, message }
  );

// Update shipping method (seller)
export const updateOrderShipping = async (
  user: Nullable<UserWithToken>,
  orderId: number,
  shippingMethod: string,
  shippingCost?: string,
  message?: string
) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation updateOrderShipping(
        $orderId: Int!
        $shippingMethod: String!
        $shippingCost: BigInt
        $message: String
      ) {
        data: updateOrderShipping(
          orderId: $orderId
          shippingMethod: $shippingMethod
          shippingCost: $shippingCost
          message: $message
        ) {
          ...OrderFields
        }
      }
    `,
    { orderId, shippingMethod, shippingCost, message }
  );

// Add a shipment leg (seller). `input.sequence` is ignored here: the next
// number in the sequence is assigned automatically.
export const addOrderShipment = async (
  user: Nullable<UserWithToken>,
  orderId: number,
  input: OrderShipmentInput
) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation addOrderShipment($orderId: Int!, $input: OrderShipmentInput!) {
        data: addOrderShipment(orderId: $orderId, input: $input) {
          ...OrderFields
        }
      }
    `,
    { orderId, input }
  );

// Update a shipment leg (seller). `input.sequence` is honoured here, unlike
// on addOrderShipment.
export const updateOrderShipment = async (
  user: Nullable<UserWithToken>,
  shipmentId: number,
  input: OrderShipmentInput
) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation updateOrderShipment($shipmentId: Int!, $input: OrderShipmentInput!) {
        data: updateOrderShipment(shipmentId: $shipmentId, input: $input) {
          ...OrderFields
        }
      }
    `,
    { shipmentId, input }
  );

// Delete a shipment leg (seller)
export const deleteOrderShipment = async (user: Nullable<UserWithToken>, shipmentId: number) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation deleteOrderShipment($shipmentId: Int!) {
        data: deleteOrderShipment(shipmentId: $shipmentId) {
          ...OrderFields
        }
      }
    `,
    { shipmentId }
  );

// Mark order as delivered (buyer)
export const markOrderAsDelivered = async (user: Nullable<UserWithToken>, orderId: number, message?: string) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation markOrderAsDelivered($orderId: Int!, $message: String) {
        data: markOrderAsDelivered(orderId: $orderId, message: $message) {
          ...OrderFields
        }
      }
    `,
    { orderId, message }
  );

// Cancel order
export const cancelOrder = async (user: Nullable<UserWithToken>, orderId: number, reason: string) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation cancelOrder($orderId: Int!, $reason: String!) {
        data: cancelOrder(orderId: $orderId, reason: $reason) {
          ...OrderFields
        }
      }
    `,
    { orderId, reason }
  );

// Escotilla de admin: mueve el pedido a cualquier estado (adelante o atrás) y
// reescribe las fechas de los hitos. El API la rebota a cualquiera que no sea
// admin, así que la UI la muestra sólo para ellos.
//
// Los dos argumentos son opcionales por separado: sólo `dates` corrige las
// fechas de un pedido ya cargado, sólo `status` lo mueve sin inventar fechas.
export const overrideOrderStatus = async (
  user: Nullable<UserWithToken>,
  orderId: number,
  input: {
    status?: OrderStatus;
    dates?: OrderMilestoneDatesInput;
    reason?: string;
  }
) =>
  getGraphQLQueryClient(user).request<WithData<Order>>(
    gql`
      ${ORDER_FRAGMENT}
      mutation overrideOrderStatus(
        $orderId: Int!
        $status: OrderStatus
        $dates: OrderMilestoneDatesInput
        $reason: String
      ) {
        data: overrideOrderStatus(
          orderId: $orderId
          status: $status
          dates: $dates
          reason: $reason
        ) {
          ...OrderFields
        }
      }
    `,
    { orderId, ...input }
  );

// Add message to order
export const addOrderMessage = async (user: Nullable<UserWithToken>, orderId: number, message: string) =>
  getGraphQLQueryClient(user).request<WithData<OrderEvent>>(
    gql`
      ${ORDER_PARTICIPANT_FRAGMENT}
      ${ORDER_EVENT_FRAGMENT}
      mutation addOrderMessage($orderId: Int!, $message: String!) {
        data: addOrderMessage(orderId: $orderId, message: $message) {
          ...OrderEventFields
        }
      }
    `,
    { orderId, message }
  );

// Submit order review (buyer only)
export const submitOrderReview = async (
  user: Nullable<UserWithToken>,
  orderId: number,
  input: OrderReviewInput
) =>
  getGraphQLQueryClient(user).request<WithData<OrderReview>>(
    gql`
      ${ORDER_REVIEW_FRAGMENT}
      mutation submitOrderReview($orderId: Int!, $input: OrderReviewInput!) {
        data: submitOrderReview(orderId: $orderId, input: $input) {
          ...OrderReviewFields
        }
      }
    `,
    { orderId, input }
  );

// Submit buyer review (seller only)
export const submitBuyerReview = async (
  user: Nullable<UserWithToken>,
  orderId: number,
  input: OrderBuyerReviewInput
) =>
  getGraphQLQueryClient(user).request<WithData<OrderBuyerReview>>(
    gql`
      ${ORDER_BUYER_REVIEW_FRAGMENT}
      mutation submitBuyerReview($orderId: Int!, $input: OrderBuyerReviewInput!) {
        data: submitBuyerReview(orderId: $orderId, input: $input) {
          ...OrderBuyerReviewFields
        }
      }
    `,
    { orderId, input }
  );

// Get seller ratings (public)
export const getSellerRatings = async (sellerId: number) =>
  getGraphQLQueryClient(null).request<{ data: SellerRatings }>(
    gql`
      query sellerRatings($sellerId: Int!) {
        data: sellerRatings(sellerId: $sellerId) {
          totalReviews
          averageCommunication
          averageShipping
          averageProduct
          averageOverall
        }
      }
    `,
    { sellerId }
  );

// Get buyer ratings (public)
export const getBuyerRatings = async (buyerId: number) =>
  getGraphQLQueryClient(null).request<{ data: BuyerRatings }>(
    gql`
      query buyerRatings($buyerId: Int!) {
        data: buyerRatings(buyerId: $buyerId) {
          totalReviews
          averageCommunication
          averagePaymentCommitment
          averageDeliveryCommitment
          averageOverall
        }
      }
    `,
    { buyerId }
  );
